/**
 * Persistence primitive. Raw `localStorage` is banned by lint; everything goes
 * through here.
 *
 * LESSON PINNED AT THE SCENE (Journal post-mortem §2, §4): `localStorage.setItem`
 * throws on quota exhaustion, in Safari private mode, and when storage is blocked
 * by policy. A bare call inside a `try {} catch {}` is how a device silently stops
 * persisting anything — including the error ledger that would have reported it.
 * Every operation here returns an explicit outcome; callers must handle failure,
 * and the ledger has a memory fallback so it can still report its own disk death.
 */
/** In-memory StorageLike. Used in tests, and as the ledger's last-resort fallback. */
export class MemoryStorage {
    map = new Map();
    getItem(key) {
        return this.map.get(key) ?? null;
    }
    setItem(key, value) {
        this.map.set(key, value);
    }
    removeItem(key) {
        this.map.delete(key);
    }
    key(index) {
        return [...this.map.keys()][index] ?? null;
    }
    get length() {
        return this.map.size;
    }
}
const QUOTA_ERROR_NAMES = new Set([
    'QuotaExceededError',
    'NS_ERROR_DOM_QUOTA_REACHED', // Firefox
]);
function classifyStorageFailure(error) {
    const name = typeof error === 'object' && error !== null ? error.name : undefined;
    const code = typeof error === 'object' && error !== null ? error.code : undefined;
    // Name/code first, per the same rule as the error classifier.
    if (typeof name === 'string' && QUOTA_ERROR_NAMES.has(name))
        return 'quota';
    if (code === 22 || code === 1014)
        return 'quota';
    if (typeof name === 'string' && name === 'SecurityError')
        return 'unavailable';
    return 'unknown';
}
export function safeGet(storage, key) {
    try {
        return storage.getItem(key);
    }
    catch {
        // A read that throws means storage is unavailable, not that the key is absent.
        // Callers distinguish the two via safeProbe() when it matters.
        return null;
    }
}
export function safeSet(storage, key, value) {
    try {
        storage.setItem(key, value);
        return { ok: true };
    }
    catch (error) {
        return { ok: false, reason: classifyStorageFailure(error), error };
    }
}
export function safeRemove(storage, key) {
    try {
        storage.removeItem(key);
        return { ok: true };
    }
    catch (error) {
        return { ok: false, reason: classifyStorageFailure(error), error };
    }
}
/** Is this storage actually usable? Distinguishes "empty" from "blocked". */
export function safeProbe(storage) {
    const probeKey = '__mononoke_probe__';
    const written = safeSet(storage, probeKey, '1');
    if (!written.ok)
        return false;
    safeRemove(storage, probeKey);
    return true;
}
//# sourceMappingURL=storage.js.map